/**
 * @file Axios instance с JWT interceptor и silent refresh
 * @see docs/tz/00-architecture.md §7.2 (Session Management)
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true, // httpOnly cookie для refresh token
});

// Request interceptor — добавить Authorization header
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — silent refresh при 401
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  refreshQueue = [];
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Не retry для login/refresh/logout
    if (
      !originalRequest ||
      error.response?.status !== 401 ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/logout') ||
      (originalRequest as { _retry?: boolean })._retry
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({
          resolve: (token: string) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          },
          reject,
        });
      });
    }

    isRefreshing = true;
    (originalRequest as { _retry?: boolean })._retry = true;

    try {
      const { data } = await api.post('/auth/refresh');
      const newToken = data.accessToken;
      localStorage.setItem('access_token', newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      processQueue(null, newToken);
      // Синхронизировать новый токен между вкладками
      try {
        const bc = new BroadcastChannel('auth');
        bc.postMessage({ type: 'token_refreshed', token: newToken });
        bc.close();
      } catch { /* BroadcastChannel не поддерживается */ }
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      localStorage.removeItem('access_token');
      window.location.href = '/login';
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

// ── BroadcastChannel — синхронизация auth между вкладками ──
// @see docs/tz/01-phase-mvp.md §1.3 (BroadcastChannel auth sync)
try {
  const authChannel = new BroadcastChannel('auth');
  authChannel.onmessage = (event) => {
    const { type } = event.data || {};
    if (type === 'logout') {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    } else if (type === 'token_refreshed' && event.data.token) {
      localStorage.setItem('access_token', event.data.token);
    }
  };
} catch {
  // Fallback: localStorage event для старых браузеров
  window.addEventListener('storage', (event) => {
    if (event.key === 'access_token' && event.newValue === null) {
      window.location.href = '/login';
    }
  });
}

export default api;
